<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <div>
    <div class="action-key-container">
      <label class="tb-label hot-keys">Hot keys<br>
        <span class="hot-keys-desc">Trigger hotkey by pressing buttons or use keyboard shortcuts as shown.</span>
      </label>

      <div class="action-keys">
        <actionKey
          v-for="(action, index) in actions"
          :key="index"
          :action="action"
          :production_context="production_context"
          :data-test="`graphics-editor-timer-${action.hotkey.toLowerCase()}`"
        />
      </div>
    </div>

    <label v-if="counterFormat == 'Time'">Duration</label>

    <div class="timer-preview-group" v-if="counterFormat == 'Time'">
      <div class="timer-preview-select">
        <div class="timer-select">
          <select
            class="tb-select"
            v-model="hourPreview"
            data-entity="producer-editor-selectHourPreview"
            data-test="graphics-editor-timer-hourDropdown"
          >
            <option
              v-for="hour in hoursList"
              :key="hour"
            >
              {{ hour }}
            </option>
          </select>
        </div>

        <label class="sidebar-input-sub-label">H</label>
      </div>

      <div class="timer-preview-select">
        <div class="timer-select">
          <select
            class="tb-select"
            v-model="minutePreview"
            data-entity="producer-editor-selectMinutePreview"
            data-test="graphics-editor-timer-minuteDropdown"
          >
            <option
              v-for="minute in minutesList"
              :key="minute"
            >
              {{ minute }}
            </option>
          </select>
        </div>

        <label class="sidebar-input-sub-label">M</label>
      </div>

      <div class="timer-preview-select">
        <div class="timer-select">
          <select
            class="tb-select"
            v-model="secondPreview"
            data-entity="producer-editor-selectSecondPreview"
            data-test="graphics-editor-timer-secondDropdown"
          >
            <option
              v-for="second in secondsList"
              :key="second"
            >
              {{ second }}
            </option>
          </select>
        </div>

        <label class="sidebar-input-sub-label">S</label>
      </div>
    </div>

    <div v-if="counterFormat == 'Date & Time'" class='datepicker'>
      <date-picker
        v-model="dateTimeModel"
        minimum-view="time"
        input-format="MM/dd/yyyy hh:mm aa"
        :clearable="false"
        :starting-view-date="safeDefaultDate"
        :disabled-dates="disabledDatesOptions"
        :disabled-time="disabledTimeOptions"
        placeholder="Select date and time"
        data-entity="producer-editor-dateTimePicker"
        data-test="graphics-editor-timer-picker"
      />
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import DatePicker from 'vue3-datepicker';

import ActionKey from '../../../../panel-editor/hotkeys/ActionKey.vue';

const STORED_DATE_TIME_FORMAT = 'MM/DD/YYYY hh:mm A';

dayjs.extend(customParseFormat);

export default {
  props: ['element', 'graphic_fields', 'production_context'],

  components: {
    DatePicker,
    ActionKey,
  },

  data() {
    return {
      hoursList: [],
      minutesList: [],
      secondsList: [],
      hourCutoffPreview: 0,
      minuteCutoffPreview: 0,
      secondCutoffPreview: 0,
      actions: [
        {
          hotkey: 'S',
          display: 'Start',
          description: 'Use this hotkey to start the timer',
          value: 'start',
          type: 'timer widget',
          elementId: this.element.id,
        },
        {
          hotkey: 'P',
          display: 'Pause',
          description: 'Use this hotkey to pause the timer',
          value: 'pause',
          type: 'timer widget',
          elementId: this.element.id,
        },
        {
          hotkey: 'R',
          display: 'Reset',
          description: 'Use this hotkey to reset the timer',
          value: 'reset',
          type: 'timer widget',
          elementId: this.element.id,
        },
      ],
    };
  },

  computed: {
    elementFields() {
      return this.graphic_fields?.[this.element.id] || {};
    },

    counterFormat() {
      return this.getFieldValue('counterFormat');
    },

    timerType() {
      return this.getFieldValue('timerType');
    },

    playBtn() {
      const { play, timerType } = this.element;

      if (play) {
        return timerType === 'Stopwatch' ? 'Stop' : 'Pause';
      }

      return timerType === 'Stopwatch' ? 'Start' : 'Play';
    },

    dateTimeModel: {
      get() {
        const dateTime = this.getFieldValue('dateTime');

        if (!dateTime) {
          return null;
        }

        const parsed = typeof dateTime === 'string'
          ? dayjs(dateTime, STORED_DATE_TIME_FORMAT, true)
          : dayjs(dateTime);
        return parsed.isValid() ? parsed.toDate() : null;
      },
      set(value) {
        this.setDateTime(value);
      },
    },

    hourPreview: {
      get() {
        return this.getFieldValue('hourPreview') ?? 0;
      },
      set(value) {
        this.updateField('hourPreview', parseInt(value, 10));
      },
    },

    minutePreview: {
      get() {
        return this.getFieldValue('minutePreview') ?? 0;
      },
      set(value) {
        this.updateField('minutePreview', parseInt(value, 10));
      },
    },

    secondPreview: {
      get() {
        return this.getFieldValue('secondPreview') ?? 0;
      },
      set(value) {
        this.updateField('secondPreview', parseInt(value, 10));
      },
    },

    safeDefaultDate() {
      const dateTime = this.getFieldValue('dateTime');
      const parsed = typeof dateTime === 'string'
        ? dayjs(dateTime, STORED_DATE_TIME_FORMAT, true)
        : dayjs(dateTime);

      return parsed.isValid() ? parsed.toDate() : new Date();
    },

    disabledDatesOptions() {
      return {
        predicate: (date) => this.isDisabledDate(date),
      };
    },

    disabledTimeOptions() {
      return {
        predicate: (date) => this.isDisabledTime(date),
      };
    },
  },

  mounted() {
    // create values for select menus
    for (let i = 0; i < 60; i += 1) {
      if (i < 24) this.hoursList.push(i);
      this.minutesList.push(i);
      this.secondsList.push(i);
    }

    // reset element id if we select a new layer while the layer settings panel is already open
    // vue has poor reactivity support for values in arrays
    this.$watch(() => this.element, () => {
      this.actions.forEach((action) => {
        action.elementId = this.element.id; // eslint-disable-line no-param-reassign
      });
    });
  },

  methods: {
    updateField(field, value) {
      return this.$emit('value', {
        value,
        field,
      });
    },

    getFieldValue(field) {
      if (Object.prototype.hasOwnProperty.call(this.elementFields, field)) {
        return this.elementFields[field];
      }

      if (Object.prototype.hasOwnProperty.call(this.element || {}, field)) {
        return this.element[field];
      }

      return null;
    },

    setDateTime(value) {
      const formattedValue = value ? dayjs(value).format(STORED_DATE_TIME_FORMAT) : null;

      return this.updateField('dateTime', formattedValue);
    },

    /**
    * Returns true if date should be disabled
    */
    isDisabledDate(date) {
      const targetDate = dayjs(date);
      if (!targetDate.isValid()) {
        return false;
      }

      const todayStart = dayjs().startOf('day');

      if (this.timerType === 'Countdown') {
        return targetDate.isBefore(todayStart);
      }

      return targetDate.isAfter(todayStart);
    },

    isDisabledTime(date) {
      const targetDate = dayjs(date);
      if (!targetDate.isValid()) {
        return false;
      }

      if (this.timerType === 'Countdown') {
        return targetDate.isBefore();
      }

      return targetDate.isAfter();
    },
  },
};
</script>
